import React from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Landmark, ScrollText, MapPin, Globe, Library } from 'lucide-react';

const SOURCE_TYPE_CONFIG = {
  archaeology: { labelKey: 'sources.archaeology', icon: Landmark, color: '#D97706', bg: 'rgba(217,119,6,0.12)' },
  ane_parallel: { labelKey: 'sources.ane_parallel', icon: ScrollText, color: '#6366F1', bg: 'rgba(99,102,241,0.12)' },
  manuscript: { labelKey: 'sources.manuscript', icon: BookOpen, color: '#059669', bg: 'rgba(5,150,105,0.12)' },
  geography: { labelKey: 'sources.geography', icon: MapPin, color: '#0284C7', bg: 'rgba(2,132,199,0.12)' },
  josephus: { labelKey: 'sources.josephus', icon: Library, color: '#9333EA', bg: 'rgba(147,51,234,0.12)' },
  edersheim: { labelKey: 'sources.edersheim', icon: Library, color: '#DC2626', bg: 'rgba(220,38,38,0.12)' },
  septuagint: { labelKey: 'sources.septuagint', icon: Globe, color: '#0891B2', bg: 'rgba(8,145,178,0.12)' },
  church_fathers: { labelKey: 'sources.church_fathers', icon: Library, color: '#B45309', bg: 'rgba(180,83,9,0.12)' },
  dead_sea_scrolls: { labelKey: 'sources.dead_sea_scrolls', icon: ScrollText, color: '#065F46', bg: 'rgba(6,95,70,0.12)' },
  isbe: { labelKey: 'sources.isbe', icon: Library, color: '#7C3AED', bg: 'rgba(124,58,237,0.12)' },
};

export { SOURCE_TYPE_CONFIG };

/**
 * Renders a row of pill-shaped citation chips for the historical sources
 * used in an AI response. Clicking a chip opens the source drawer.
 */
export default function SourceChips({ sources, onChipClick }) {
  const { t } = useTranslation();
  if (!sources || sources.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t theme-border animate-in fade-in duration-300" data-testid="source-chips">
      <span className="text-[10px] uppercase tracking-wider font-medium self-center mr-1" style={{ color: 'var(--theme-text-secondary)', opacity: 0.6 }}>
        {t('sources.label')}
      </span>
      {sources.map((source, i) => {
        const config = SOURCE_TYPE_CONFIG[source.source_type] || SOURCE_TYPE_CONFIG.isbe;
        const Icon = config.icon;
        return (
          <button
            key={`${source.source_type}-${source.work_title}-${i}`}
            onClick={() => onChipClick(source, i)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all hover:scale-[1.04] active:scale-95 cursor-pointer border"
            style={{
              background: config.bg,
              borderColor: `${config.color}30`,
              color: config.color,
            }}
            title={`${source.work_title} — ${source.source_name}`}
            data-testid={`source-chip-${i}`}
          >
            <Icon className="h-3 w-3 flex-shrink-0" />
            <span className="truncate max-w-[140px]">{source.work_title}</span>
          </button>
        );
      })}
    </div>
  );
}
